import { z } from "zod";

/**
 * Shared-session schemas.
 *
 * Everything here crosses a trust boundary: it arrives over the network from
 * another player, or out of IndexedDB from a previous version of the app.
 * Reads are validated; writes are trusted because TypeScript already checked
 * them. A record that fails validation is dropped, never thrown on — one
 * malformed marker must not blank the whole map.
 */

export const FactionIdSchema = z.enum(["lonestar", "valkyra", "manticore"]);

/** The game's own six progression roles. Kept in step with `src/data/roles.ts`. */
export const RoleIdSchema = z.enum(["assault", "driver", "medic", "pilot", "recon", "support"]);

export const CoordSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});

/**
 * A player in the session.
 *
 * `id` is generated once per browser and kept, so reconnecting after a crash
 * restores who you were — including your command, if you held it.
 */
export const PlayerSchema = z.object({
  id: z.string().min(1).max(64),
  callsign: z.string().min(1).max(20),
  faction: FactionIdSchema,
  role: RoleIdSchema,
  /** Squad id. Created squads use a UUID, hence the longer bound. Empty means unassigned. */
  squad: z.string().max(36).default(""),
  joinedAt: z.number().int().nonnegative(),
  /** Last time this player was seen live. Used for the command grace period. */
  lastSeenAt: z.number().int().nonnegative(),
  /** What the player is currently in — empty when on foot. */
  vehicle: z.string().max(20).default(""),
});

export type Player = z.infer<typeof PlayerSchema>;

/**
 * What a placed marker looks like on the map.
 *
 * Kept identical to the placeable choices in `src/data/markers.ts`'
 * `MARKER_GROUPS` rather than a separate, more abstract set of categories —
 * the palette a player picks from and the value that gets synced and
 * rendered are the same icon, so there is exactly one place a new marker
 * type has to be added, not two that can drift apart.
 */
export const MarkerKindSchema = z.enum([
  // Callouts
  "spotted",
  "warning",
  "hill_warning",
  "lasthit",
  "like",
  // Assets
  "fob",
  "artillery",
  "spawn_vehicle",
  // Vehicles
  "quad",
  "dune",
  "kodiak",
  "ural",
  "tank",
  "havoc",
  "heli",
  // Factions
  "lonestar",
  "valkyra",
  "manticore",
]);

export type MarkerKind = z.infer<typeof MarkerKindSchema>;

export const MarkerSchema = z.object({
  id: z.string().min(1).max(64),
  kind: MarkerKindSchema,
  at: CoordSchema,
  label: z.string().max(40).default(""),
  /** Which faction this marker refers to. Absent means neutral or unknown. */
  faction: FactionIdSchema.optional(),
  createdBy: z.string().min(1).max(64),
  createdAt: z.number().int().nonnegative(),
  /** Optional radius in meters, for a hand-set range or threat ring. */
  radiusMeters: z.number().finite().nonnegative().optional(),
  /**
   * A weapon from `engine/range.ts`'s `WEAPON_PROFILES`, e.g. `"l81-mortar"`.
   * An artillery marker carrying one gets its min/max rings drawn from the
   * weapon's own figures instead of a hand-set radius, and stays correct if
   * those figures are ever revised.
   */
  weaponId: z.string().max(32).optional(),
  /**
   * True when this marks an *enemy* position — same icon, same range rings,
   * but drawn in threat colours so nobody mistakes it for friendlies.
   */
  threat: z.boolean().default(false),
  /** Which theatre map this marker belongs to. Empty for legacy records. */
  mapId: z.string().max(32).default(""),
});

export type Marker = z.infer<typeof MarkerSchema>;

export const RouteSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().max(40).default(""),
  waypoints: z.array(CoordSchema).min(2).max(64),
  createdBy: z.string().min(1).max(64),
  createdAt: z.number().int().nonnegative(),
  /** Movement profile the route is planned for, e.g. "littlebird". */
  movement: z.string().max(32).default("walk"),
  /** Which theatre map this route belongs to. Empty for legacy records. */
  mapId: z.string().max(32).default(""),
});

export type Route = z.infer<typeof RouteSchema>;

export const OrderStatusSchema = z.enum(["active", "complete", "cancelled"]);

export const OrderKindSchema = z.enum([
  "attack",
  "defend",
  "hold",
  "move",
  "recon",
  "resupply",
  "transport",
  "extract",
  "build",
]);

/**
 * An order from the commander to a squad.
 *
 * Addressed to a squad, never to a person: a commander who tasks individuals
 * has stopped commanding.
 *
 * There is deliberately no acknowledgement step. This sits on a second
 * screen; a squad that is actually fighting has no hand free to alt-tab and
 * click "seen". Voice comms already carry the "roger" — a button for it here
 * would be a slower, worse copy of that. An order is active until the squad
 * lead marks it done or the commander replaces or withdraws it.
 *
 * The text is derived from the verb and the place rather than typed, because
 * nobody composes sentences while being shot at.
 */
export const OrderSchema = z.object({
  id: z.string().min(1).max(64),
  /** Squad this order is addressed to. A UUID when the squad was created in-app. */
  squadId: z.string().min(1).max(36),
  kind: OrderKindSchema,
  at: CoordSchema.optional(),
  /** Callout reference for the location, e.g. "D5-7". */
  place: z.string().max(24).default(""),
  /** Optional free-text qualifier, kept short on purpose. */
  note: z.string().max(80).default(""),
  status: OrderStatusSchema.default("active"),
  issuedBy: z.string().min(1).max(64),
  issuedAt: z.number().int().nonnegative(),
  /** Which theatre map this order was issued on. Empty for legacy records. */
  mapId: z.string().max(32).default(""),
});

export type Order = z.infer<typeof OrderSchema>;

/**
 * A squad.
 *
 * Membership lives on the player record, so joining a squad is one write and
 * cannot leave two collections disagreeing about who is in it. What lives here
 * is only what belongs to the squad itself.
 *
 * Squads are not a fixed, pre-seeded roster — one is created the moment its
 * first member names and joins it, so `name` is the callsign that player
 * chose, not a lookup into a static list.
 */
export const SquadSchema = z.object({
  /** A UUID for squads created in-app; a short slug for anything seeded by tests. */
  id: z.string().min(1).max(36),
  /** Chosen by whoever created the squad. Falls back to the id if missing. */
  name: z.string().max(24).default(""),
  /** Player leading it. Empty when the squad has nobody in charge yet. */
  leadId: z.string().max(64).default(""),
  createdAt: z.number().int().nonnegative(),
});

export type Squad = z.infer<typeof SquadSchema>;

export const VoteSchema = z.object({
  voterId: z.string().min(1).max(64),
  inFavour: z.boolean(),
  at: z.number().int().nonnegative(),
});

export const CommandStateSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("vacant") }),
  z.object({
    kind: z.literal("held"),
    commanderId: z.string().min(1).max(64),
    since: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("contested"),
    commanderId: z.string().min(1).max(64).nullable(),
    challengerId: z.string().min(1).max(64),
    openedAt: z.number().int().nonnegative(),
    closesAt: z.number().int().nonnegative(),
  }),
]);

/** Session-wide settings every member sees. */
export const SessionMetaSchema = z.object({
  code: z.string().min(1).max(16),
  mapId: z.string().min(1).max(32),
  /** The faction this session is playing as. */
  faction: FactionIdSchema,
  createdAt: z.number().int().nonnegative(),
  schemaVersion: z.number().int().nonnegative(),
});

export type SessionMeta = z.infer<typeof SessionMetaSchema>;

/** Bump when a shared shape changes in a way older clients cannot read. */
export const SCHEMA_VERSION = 1;
